import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Award,
  ChevronDown,
  Heart,
  Megaphone,
  Play,
  Stethoscope,
  Users,
} from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { api, asset, mediaUrl, youTubeId } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Loading } from "@/components/public/Ui";
import { NominationFormCard } from "@/components/public/NominationFormCard";
import { EmeraldEventGallery } from "@/components/public/EmeraldEventGallery";
import "./emerald-honours-premium.css";

const CLIENT_EMERALD_MOMENTS = [
  "/assets/client-feedback/emerald-moment-01.webp",
  "/assets/client-feedback/emerald-moment-02.webp",
  "/assets/client-feedback/emerald-moment-03.webp",
  "/assets/client-feedback/emerald-moment-04.webp",
  "/assets/client-feedback/emerald-moment-05.webp",
  "/assets/client-feedback/emerald-moment-06.webp",
  "/assets/client-feedback/emerald-moment-07.webp",
];

const APPROVED_AWARDS = [
  {
    number: "01",
    title: "Emerald Voice for Change",
    audience: "Advocate",
    icon: Megaphone,
    summary: "Recognising a powerful voice for kidney-health awareness.",
    criteria:
      "Recognizes an individual who has shown exceptional dedication to raising awareness about kidney disease, consistently promoting kidney health through various outreach activities, and significantly impacting public knowledge and attitudes.",
  },
  {
    number: "02",
    title: "Emerald Inspiration in Action Award",
    audience: "Patient",
    icon: Heart,
    summary: "Celebrating a CKD patient whose advocacy gives others hope.",
    criteria:
      "Recognizes a CKD patient who has consistently demonstrated a strong commitment to advocating for kidney disease awareness, who has shown exceptional dedication to providing hope and encouragement to others, motivating others to support the BKA’s mission, and engaging with the BKA to support its programs and initiatives.",
  },
  {
    number: "03",
    title: "Emerald Gift of Life",
    audience: "Donor",
    icon: Award,
    summary: "Honouring extraordinary courage, generosity and the gift of life.",
    criteria:
      "Recognizes a donor who has provided a profound sense of hope to a recipient and their family. This award honors a kidney donor whose selfless act of generosity exemplifies the highest standards of human kindness. This donor has shown extraordinary courage and selflessness in donating a kidney, making a lasting contribution to the recipient’s quality of life.",
  },
  {
    number: "04",
    title: "Emerald Pillar of Strength Award",
    audience: "Support",
    icon: Users,
    summary: "Recognising the family and caregivers who stand strong beside patients.",
    criteria:
      "Recognizes a family member who has gone above and beyond in their caregiving role. This award acknowledges their commitment to managing the day-to-day needs of the CKD patient, including medical care, dietary management, and emotional support. It honors their unwavering dedication, resilience, and strength.",
  },
  {
    number: "05",
    title: "Ada Thompson Emerald Award",
    audience: "Medical Professional",
    icon: Stethoscope,
    summary: "Celebrating leadership and innovation in kidney care.",
    criteria:
      "Awarded to a healthcare professional who has made significant contributions to the field of kidney disease and has exhibited leadership and innovation in kidney care, consistently working to improve patient outcomes. This individual demonstrates a strong commitment to improving the quality of life for dialysis patients and supports the mission of the BKA.",
  },
];

const reveal = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0 },
};

function AwardCard({ award, index, open, onToggle }) {
  const Icon = award.icon || Award;

  return (
    <motion.article
      variants={reveal}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.52, delay: index * 0.06 }}
      className={`eh-award-card ${open ? "is-open" : ""}`}
      data-testid={`emerald-award-${index}`}
    >
      <div className="eh-award-card__top">
        <span className="eh-award-card__number">{award.number}</span>
        <span className="eh-award-card__icon" aria-hidden="true">
          <Icon />
        </span>
      </div>

      <p className="eh-award-card__audience">{award.audience}</p>
      <h3>{award.title}</h3>
      <p className="eh-award-card__summary">{award.summary}</p>

      <button
        type="button"
        className="eh-award-card__toggle"
        onClick={onToggle}
        aria-expanded={open}
      >
        {open ? "Hide Criteria" : "View Criteria"}
        <ChevronDown className={open ? "rotate-180" : ""} />
      </button>

      <div className={`eh-award-card__criteria ${open ? "is-open" : ""}`}>
        <p>{award.criteria}</p>
      </div>
    </motion.article>
  );
}

export default function EmeraldHonours() {
  const [page, setPage] = useState(null);
  const [media, setMedia] = useState([]);
  const [emeraldVideos, setEmeraldVideos] = useState([]);
  const [galleryRecords, setGalleryRecords] = useState([]);
  const [playing, setPlaying] = useState(null);
  const [openAward, setOpenAward] = useState(0);
  const [momentIndex, setMomentIndex] = useState(0);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    let mounted = true;

    // CMS content must always be fresh: the timestamp stops a browser/CDN/proxy from
    // replaying the previous Emerald payload after an administrator publishes a photo,
    // and the focus listener re-fetches when the admin switches back from the CMS tab.
    const loadCms = () => {
      const bust = { params: { _: Date.now() } };

      api
        .get("/public/pages/emerald-honours", bust)
        .then((r) => mounted && setPage(r.data))
        .catch(() => mounted && setPage(false));

      api
        .get("/public/emerald/media", bust)
        .then((r) => mounted && setMedia(r.data || []))
        .catch(() => mounted && setMedia([]));

      api
        .get("/public/videos", { params: { placement: "emerald", _: Date.now() } })
        .then((r) => mounted && setEmeraldVideos(r.data || []))
        .catch(() => mounted && setEmeraldVideos([]));

      api
        .get("/public/emerald/gallery", bust)
        .then((r) => mounted && setGalleryRecords(r.data || []))
        .catch(() => mounted && setGalleryRecords([]));
    };

    loadCms();
    window.addEventListener("focus", loadCms);
    return () => {
      mounted = false;
      window.removeEventListener("focus", loadCms);
    };
  }, []);

  useEffect(() => {
    if (reduceMotion || CLIENT_EMERALD_MOMENTS.length < 2) return undefined;
    const timer = window.setInterval(() => {
      setMomentIndex((current) => (current + 1) % CLIENT_EMERALD_MOMENTS.length);
    }, 4400);
    return () => window.clearInterval(timer);
  }, [reduceMotion]);

  const b = page?.body_json || {};
  const hero = b.hero || {};

  const awards = useMemo(() => {
    const fromCms = Array.isArray(b.award_categories) ? b.award_categories : [];
    if (!fromCms.length) return APPROVED_AWARDS;

    return APPROVED_AWARDS.map((fallback, index) => {
      const row = fromCms[index] || {};
      return {
        ...fallback,
        ...row,
        number: row.number || fallback.number,
        title: row.title || row.name || fallback.title,
        audience: row.audience || row.type || fallback.audience,
        summary: row.summary || fallback.summary,
        criteria: row.criteria || row.description || fallback.criteria,
        icon: fallback.icon,
      };
    });
  }, [b.award_categories]);

  // Merge BOTH CMS keys — never `b.gallery || b.event_gallery`, because an empty
  // array is truthy in JavaScript and would hide populated records.
  const pageGallery = [
    ...(Array.isArray(b.event_gallery) ? b.event_gallery : []),
    ...(Array.isArray(b.gallery) ? b.gallery : []),
  ];

  // CMS gallery records win; the page body_json gallery is the second source;
  // the approved client event photographs guarantee the section is never empty.
  const eventGallery = useMemo(() => {
    const fromCms = (galleryRecords || [])
      .slice()
      .sort((a, b2) => (a.display_order ?? 0) - (b2.display_order ?? 0))
      .map((row, index) => ({
        src: mediaUrl(row.image),
        thumb: row.thumbnail ? mediaUrl(row.thumbnail) : mediaUrl(row.image),
        caption: row.caption || "",
        alt: row.caption || `Emerald Honours event photograph ${index + 1}`,
        featured: Boolean(row.featured),
      }))
      .filter((row) => row.src);

    if (fromCms.length) {
      const featuredAt = fromCms.findIndex((row) => row.featured);
      if (featuredAt > 0) {
        const [primary] = fromCms.splice(featuredAt, 1);
        fromCms.unshift(primary);
      }
      // Keep the editorial composition complete (feature + 2x2) while BKA is still
      // uploading: top up with the approved event photographs, never with gaps.
      if (fromCms.length < 5) {
        const used = new Set(fromCms.map((row) => row.src));
        CLIENT_EMERALD_MOMENTS.forEach((src, index) => {
          const resolved = asset(src);
          if (fromCms.length < 5 && !used.has(resolved)) {
            fromCms.push({
              src: resolved,
              thumb: resolved,
              caption: "",
              alt: `Emerald Honours event photograph ${index + 1}`,
            });
          }
        });
      }
      return fromCms;
    }

    const fromPage = pageGallery
      .map((item, index) => {
        if (typeof item === "string") {
          return {
            src: mediaUrl(item),
            thumb: mediaUrl(item),
            caption: "",
            alt: `Emerald Honours event photograph ${index + 1}`,
            order: index,
            featured: false,
          };
        }
        if (!item || typeof item !== "object") return null;

        const published = item.published ?? item.is_published ?? item.active ?? true;
        if (!published) return null;

        const src =
          item.url || item.image_url || item.image || item.src || item.file_url || item.thumbnail_url;
        if (!src) return null;

        const caption = item.caption || item.alt || item.title || "";
        return {
          src: mediaUrl(src),
          thumb: mediaUrl(item.thumbnail_url || item.thumbnail || src),
          caption,
          alt: caption || `Emerald Honours event photograph ${index + 1}`,
          order: Number(item.display_order ?? item.order ?? item.sort_order ?? index),
          featured: Boolean(item.featured ?? item.is_featured),
        };
      })
      .filter(Boolean)
      .sort((x, y) => (x.featured === y.featured ? x.order - y.order : x.featured ? -1 : 1));

    if (fromPage.length) return fromPage;

    return CLIENT_EMERALD_MOMENTS.map((src, index) => ({
      src: asset(src),
      thumb: asset(src),
      caption: "",
      alt: `Emerald Honours event photograph ${index + 1}`,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [galleryRecords, JSON.stringify(pageGallery)]);

  // Videos library (placement "emerald") plus any legacy Emerald media records.
  const emeraldMediaItems = [
    ...emeraldVideos.map((v) => ({
      id: v.id,
      title: v.title,
      url: v.url,
      thumbnail_url: v.thumbnail_url || "",
      duration: v.caption || "",
    })),
    ...media.filter((m) => !emeraldVideos.some((v) => v.youtube_id && m.url && m.url.includes(v.youtube_id))),
  ];

  if (page === null) return <Loading label="Loading Emerald Honours" />;
  const handleTilt = (event) => {
    if (reduceMotion) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width - 0.5;
    const y = (event.clientY - rect.top) / rect.height - 0.5;
    setTilt({ x: y * -7, y: x * 9 });
  };

  const resetTilt = () => setTilt({ x: 0, y: 0 });

  const honourees =
    (Array.isArray(b.honourees) && b.honourees) ||
    (Array.isArray(b.past_honourees) && b.past_honourees) ||
    [];

  return (
    <div className="eh-page" data-testid="emerald-honours-page">
      <Seo
        title={page?.seo_title || "Emerald Honours | Bahamas Kidney Association"}
        description={page?.seo_description}
        canonicalPath="/emerald-honours"
      />

      {/* HERO — 2.5D premium treatment, no WebGL */}
      <section className="eh-hero">
        {page?.hero_image_url && (
          <img
            src={mediaUrl(page.hero_image_url)}
            alt=""
            aria-hidden="true"
            className="eh-hero__background-image"
          />
        )}
        <div className="eh-hero__veil" aria-hidden="true" />
        <div className="eh-hero__orb eh-hero__orb--one" aria-hidden="true" />
        <div className="eh-hero__orb eh-hero__orb--two" aria-hidden="true" />
        <div className="eh-hero__linework" aria-hidden="true" />

        <div className="bka-container eh-hero__grid">
          <motion.div
            className="eh-hero__copy"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.58 }}
          >
            <span className="eh-kicker">
              {hero.overline || "Bahamas Kidney Association"}
            </span>
            <span className="eh-badge">Emerald Honours</span>

            <h1>
              Celebrating Courage.
              <br />
              <span>Service. Impact.</span>
            </h1>

            <p>
              {hero.subheading ||
                "Honouring advocates, patients, donors, caregivers and healthcare professionals whose courage and service move kidney health forward in The Bahamas."}
            </p>

            <div className="eh-hero__actions">
              <Link
                to="/emerald-honours/nominate"
                className="eh-btn eh-btn--gold"
                data-testid="emerald-hero-nominate"
              >
                Nominate Someone <ArrowRight />
              </Link>
              <a href="#awards" className="eh-btn eh-btn--glass">
                Explore the Awards <ArrowRight />
              </a>
            </div>
          </motion.div>

          <motion.div
            className="eh-stage"
            initial={{ opacity: 0, scale: 0.94, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.72, delay: 0.08 }}
            onMouseMove={handleTilt}
            onMouseLeave={resetTilt}
            style={{
              "--tilt-x": `${tilt.x}deg`,
              "--tilt-y": `${tilt.y}deg`,
            }}
          >
            <div className="eh-stage__halo" aria-hidden="true" />
            <div className="eh-stage__ring eh-stage__ring--one" aria-hidden="true" />
            <div className="eh-stage__ring eh-stage__ring--two" aria-hidden="true" />
            <div className="eh-stage__spark eh-stage__spark--one" aria-hidden="true" />
            <div className="eh-stage__spark eh-stage__spark--two" aria-hidden="true" />

            <div className="eh-stage__moment-shell">
              <AnimatePresence mode="wait">
                <motion.img
                  key={CLIENT_EMERALD_MOMENTS[momentIndex]}
                  src={asset(CLIENT_EMERALD_MOMENTS[momentIndex])}
                  alt="Emerald Honours campaign visual"
                  className="eh-stage__moment-image"
                  initial={{ opacity: 0, scale: 1.055 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.68, ease: [0.22, 1, 0.36, 1] }}
                />
              </AnimatePresence>
              <div className="eh-stage__moment-shade" aria-hidden="true" />
              <div className="eh-stage__moment-copy">
                <span>Emerald Honours</span>
                <strong>Celebrating Impact</strong>
              </div>
            </div>

            <div className="eh-stage__award-float" aria-hidden="true">
              <img
                src={asset("/assets/emerald-honours-award-transparent.png")}
                alt=""
                decoding="async"
              />
            </div>

            <div className="eh-stage__moment-dots" aria-label="Emerald Honours visual gallery">
              {CLIENT_EMERALD_MOMENTS.map((item, index) => (
                <button
                  type="button"
                  key={item}
                  className={index === momentIndex ? "is-active" : ""}
                  aria-label={`Show Emerald visual ${index + 1}`}
                  data-testid={`eh-moment-dot-${index}`}
                  onClick={() => setMomentIndex(index)}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="eh-moment-ribbon" aria-label="Emerald Honours visual highlights">
        <div className="eh-moment-ribbon__track">
          {[...CLIENT_EMERALD_MOMENTS, ...CLIENT_EMERALD_MOMENTS].map((item, index) => (
            <div className="eh-moment-ribbon__item" key={`${item}-${index}`} aria-hidden={index >= CLIENT_EMERALD_MOMENTS.length}>
              <img src={asset(item)} alt={index < CLIENT_EMERALD_MOMENTS.length ? "Emerald Honours visual" : ""} loading="lazy" />
            </div>
          ))}
        </div>
      </section>

      {/* BRIDGE */}
      <section className="eh-bridge">
        <div className="bka-container eh-bridge__grid">
          <motion.div
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.35 }}
            transition={{ duration: 0.52 }}
          >
            <p className="eh-overline">Recognition with purpose</p>
            <h2>Honouring the people who move kidney health forward.</h2>
          </motion.div>

          <motion.p
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.35 }}
            transition={{ duration: 0.52, delay: 0.08 }}
          >
            Emerald Honours celebrates courage, generosity, advocacy and professional
            excellence while bringing the stories behind kidney health into the public
            conversation.
          </motion.p>
        </div>
      </section>

      {/* EVENT GALLERY — CMS-managed, with approved client photographs as fallback */}
      <EmeraldEventGallery
        items={eventGallery}
        year={b.gallery_year || "2026"}
        credit={b.photography_credit || "T. Glinton Photography"}
      />

      {/* AWARDS */}
      <section className="eh-awards" id="awards">
        <div className="bka-container">
          <motion.div
            className="eh-section-head"
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.52 }}
          >
            <p className="eh-overline">Five distinctions. One shared impact.</p>
            <h2>The Emerald Honours Awards</h2>
            <p>
              Each category recognises a different form of courage, service and leadership
              within the kidney-health community.
            </p>
          </motion.div>

          <div className="eh-awards__grid">
            {awards.map((award, index) => (
              <AwardCard
                key={`${award.title}-${index}`}
                award={award}
                index={index}
                open={openAward === index}
                onToggle={() => setOpenAward(openAward === index ? -1 : index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* IMPACT / MEDIA — only shown once BKA publishes approved videos */}
      {emeraldMediaItems.length > 0 && (
      <section className="eh-impact" id="media" data-testid="emerald-media-section">
        <div className="bka-container">
          <div className="eh-section-head eh-section-head--light">
            <p className="eh-overline">Emerald Honours in action</p>
            <h2>Stories, moments and voices of impact.</h2>
          </div>

          <div className="eh-media-grid">
            {emeraldMediaItems.slice(0, 3).map((item, index) => {
                const videoId = youTubeId(item.url || "");
                return (
                  <motion.article
                    key={item.id}
                    className={index === 0 ? "eh-video-card eh-video-card--featured" : "eh-video-card"}
                    variants={reveal}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.5, delay: index * 0.07 }}
                  >
                    <div className="eh-video-card__media">
                      {playing === item.id && videoId ? (
                        <iframe
                          title={item.title}
                          src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1`}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
                          allowFullScreen
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => setPlaying(item.id)}
                          aria-label={`Play ${item.title}`}
                        >
                          {item.thumbnail_url ? (
                            <img src={mediaUrl(item.thumbnail_url)} alt="" loading="lazy" />
                          ) : videoId ? (
                            <img src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`} alt="" loading="lazy" />
                          ) : (
                            <div className="eh-video-card__fallback" aria-hidden="true" />
                          )}
                          <span className="eh-play"><Play /></span>
                        </button>
                      )}
                    </div>
                    <div className="eh-video-card__copy">
                      <p className="eh-overline">{item.duration || "Emerald Story"}</p>
                      <h3>{item.title}</h3>
                    </div>
                  </motion.article>
                );
              })}
            </div>
        </div>
      </section>
      )}

      {/* HONOUREES — do not display an unfinished empty box to public visitors */}
      {honourees.length > 0 && (
      <section className="eh-honourees">
        <div className="bka-container">
          <div className="eh-section-head">
            <p className="eh-overline">Recognition</p>
            <h2>{b.honourees_heading || "Past Honourees"}</h2>
            <p>Celebrating the people whose actions have created lasting impact.</p>
          </div>

          <div className="eh-honourees__grid">
            {honourees.map((item, index) => (
                <motion.article
                  key={item.id || `${item.name}-${index}`}
                  className="eh-honouree-card"
                  variants={reveal}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.48, delay: index * 0.05 }}
                >
                  <div className="eh-honouree-card__image">
                    {item.image_url ? (
                      <img src={mediaUrl(item.image_url)} alt={item.name || "Past honouree"} loading="lazy" />
                    ) : (
                      <Award aria-hidden="true" />
                    )}
                  </div>
                  <div className="eh-honouree-card__copy">
                    <p className="eh-overline">{item.year || "Emerald Honours"}</p>
                    <h3>{item.name}</h3>
                    <p>{item.category || item.award}</p>
                  </div>
                </motion.article>
              ))}
            </div>
        </div>
      </section>
      )}

      {/* NOMINATION JOURNEY + WORKING FORM */}
      <section className="eh-nominate" id="nominate">
        <div className="bka-container eh-nominate__grid">
          <div className="eh-nominate__intro">
            <p className="eh-overline">Nomination journey</p>
            <h2>Recognise someone whose impact deserves to be seen.</h2>
            <p>
              Tell us about someone whose work has strengthened kidney health in The Bahamas.
              Nominations are reviewed by the Bahamas Kidney Association.
            </p>

            <div className="eh-steps">
              {[
                ["01", "Nominate"],
                ["02", "Submit"],
                ["03", "Review"],
                ["04", "Honouree Selection"],
              ].map(([number, title]) => (
                <div className="eh-step" key={number}>
                  <span>{number}</span>
                  <strong>{title}</strong>
                </div>
              ))}
            </div>
          </div>

          <div className="eh-nominate__form">
            <NominationFormCard />
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="eh-final">
        <div className="eh-final__glow" aria-hidden="true" />
        <div className="bka-container eh-final__inner">
          <div>
            <p className="eh-overline">Be part of the impact</p>
            <h2>Help recognise the people moving kidney health forward.</h2>
            <p>
              Celebrate an advocate, patient, donor, caregiver or healthcare professional
              whose contribution deserves recognition.
            </p>
          </div>
          <div className="eh-final__actions">
            <Link to="/emerald-honours/nominate" className="eh-btn eh-btn--gold">
              Nominate Someone <ArrowRight />
            </Link>
            <Link to="/donate" className="eh-btn eh-btn--glass">
              Support BKA <ArrowRight />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
